import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Shield, Plus, Trash2, Loader2, AlertCircle, Check, X,
  ChevronRight, GripVertical, XCircle,
} from 'lucide-react';
import { Permissions, computeEffectivePermissions, hasPermission } from '../../../lib/permissions';

interface RolesTabProps {
  serverId: string;
  ownerId: string;
  currentUserId: string;
}

interface Role {
  id: string;
  server_id: string;
  name: string;
  color: string | null;
  permissions: number;
  position: number;
  hoist?: boolean;
  created_at: string;
  is_everyone?: boolean;
}

interface Member {
  id: string;
  user_id: string;
  roles: { id: string; name: string; permissions: number; color?: string | null; position?: number; is_everyone?: boolean; hoist?: boolean }[];
  user: { username: string; avatar_url: string | null };
}

const PERMISSION_GROUPS = [
  {
    title: 'Server',
    items: [
      { flag: Permissions.VIEW_CHANNELS, label: 'View Channels', desc: 'See channels by default' },
      { flag: Permissions.MANAGE_CHANNELS, label: 'Manage Channels', desc: 'Create, edit and delete channels' },
      { flag: Permissions.MANAGE_ROLES, label: 'Manage Roles', desc: 'Create roles and edit lower roles and channel permissions' },
      { flag: Permissions.VIEW_SERVER_LOGS, label: 'View Server Logs', desc: 'Placeholder permission for server logs' },
      { flag: Permissions.MANAGE_WEBHOOKS, label: 'Manage Webhooks', desc: 'Placeholder permission for webhooks' },
      { flag: Permissions.MANAGE_SERVER, label: 'Manage Server', desc: 'Edit server details, invites, bots and linked game server' },
    ],
  },
  {
    title: 'Members',
    items: [
      { flag: Permissions.CREATE_INVITE, label: 'Create Invite', desc: 'Create invitation links' },
      { flag: Permissions.KICK_MEMBERS, label: 'Kick Members', desc: 'Remove members and uninstall apps' },
      { flag: Permissions.BAN_MEMBERS, label: 'Ban Members', desc: 'Ban members from the server; bots cannot be banned' },
    ],
  },
  {
    title: 'Text Channels',
    items: [
      { flag: Permissions.SEND_MESSAGES, label: 'Send Messages', desc: 'Write messages in text channels' },
      { flag: Permissions.ATTACH_FILES, label: 'Attach Files', desc: 'Placeholder permission for file uploads' },
      { flag: Permissions.ADD_REACTIONS, label: 'Add Reactions', desc: 'React to messages' },
      { flag: Permissions.MANAGE_MESSAGES, label: 'Manage Messages', desc: 'Delete messages from other members' },
    ],
  },
  {
    title: 'Voice Channels',
    items: [
      { flag: Permissions.CONNECT_VOICE, label: 'Connect', desc: 'Join voice channels and hear others' },
    ],
  },
  {
    title: 'Advanced',
    items: [
      { flag: Permissions.ADMINISTRATOR, label: 'Administrator', desc: 'Grants every permission and bypasses channel restrictions' },
    ],
  },
];

const RolesTab: React.FC<RolesTabProps> = ({ serverId, ownerId, currentUserId }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Editing state
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState<string | null>(null);
  const [editPerms, setEditPerms] = useState(0);
  const [editHoist, setEditHoist] = useState(false);
  const [saving, setSaving] = useState(false);

  // Create role state
  const [creating, setCreating] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [dragRoleId, setDragRoleId] = useState<string | null>(null);

  const isOwner = currentUserId === ownerId;

  // Compute effective permissions for the current user
  const currentMember = members.find((m) => m.user_id === currentUserId);
  const effectivePerms = computeEffectivePermissions(isOwner, (currentMember?.roles || []) as { permissions: number }[]);
  const canManageRoles = hasPermission(effectivePerms, Permissions.MANAGE_ROLES);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const [rolesRes, membersRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/members`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoles(); }, [serverId]);

  const selectRole = (role: Role) => {
    setSelectedRoleId(role.id);
    setEditName(role.name);
    setEditColor(role.is_everyone ? null : role.color || null);
    setEditPerms(role.permissions || 0);
    setEditHoist(Boolean(role.hoist && !role.is_everyone));
    setError(null);
    setSuccess(null);
  };

  const handleSaveRole = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/roles/${selectedRoleId}`,
        { name: editName, color: selectedRole?.is_everyone ? null : editColor, permissions: editPerms, hoist: selectedRole?.is_everyone ? false : editHoist },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess('Role updated');
      setTimeout(() => setSuccess(null), 2000);
      await fetchRoles();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/roles`,
        { name: newRoleName.trim(), color: null, permissions: 0, hoist: false },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNewRoleName('');
      await fetchRoles();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create role');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/roles/${roleId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (selectedRoleId === roleId) setSelectedRoleId(null);
      await fetchRoles();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete role');
    }
  };

  const togglePerm = (flag: number) => {
    setEditPerms((prev) => (prev & flag ? prev & ~flag : prev | flag));
  };

  const selectedRole = roles.find((r) => r.id === selectedRoleId);
  const sortedRoles = [...roles].sort((a, b) => b.position - a.position);

  const reorderRoles = async (nextVisualOrder: Role[]) => {
    setRoles(nextVisualOrder);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/roles/reorder`,
        { role_ids: [...nextVisualOrder].sort((a, b) => a.position - b.position).map((role) => role.id) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchRoles();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reorder roles');
      await fetchRoles();
    }
  };

  const handleDropRole = (targetRoleId: string) => {
    if (!dragRoleId || dragRoleId === targetRoleId) return;
    const source = sortedRoles.find((role) => role.id === dragRoleId);
    const target = sortedRoles.find((role) => role.id === targetRoleId);
    if (!source || !target || source.is_everyone || target.is_everyone) return;
    const next = sortedRoles.filter((role) => role.id !== dragRoleId);
    const targetIndex = next.findIndex((role) => role.id === targetRoleId);
    next.splice(targetIndex, 0, source);
    const withPositions = next
      .slice()
      .reverse()
      .map((role, index) => ({ ...role, position: index }));
    reorderRoles(withPositions.sort((a, b) => b.position - a.position));
    setDragRoleId(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent-color" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Roles</h2>
        <p className="text-xs text-text-muted mt-0.5">Configure roles and permissions for your server.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30 text-success text-xs">
          <Check className="w-3.5 h-3.5 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Role list */}
      <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl overflow-hidden">
        {/* Create role */}
        {canManageRoles && (
          <div className="p-3 border-b border-border-color">
            <div className="flex gap-1.5">
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateRole()}
                className="settings-input text-xs flex-1"
                placeholder="New role name..."
              />
              <button
                onClick={handleCreateRole}
                disabled={creating || !newRoleName.trim()}
                className="px-3 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all disabled:opacity-50"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Roles */}
        {roles.length > 0 ? (
          <div className="divide-y divide-border-color">
            {sortedRoles
              .map((role) => (
                <button
                  key={role.id}
                  draggable={canManageRoles && !role.is_everyone}
                  onDragStart={() => setDragRoleId(role.id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDropRole(role.id)}
                  onClick={() => selectRole(role)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                    selectedRoleId === role.id
                      ? 'bg-accent-color/5'
                      : 'hover:bg-highlight/30'
                  }`}
                >
                  <GripVertical className={`w-4 h-4 flex-shrink-0 ${role.is_everyone ? 'text-text-muted/30' : 'text-text-muted'}`} />
                  <div className="w-3.5 h-3.5 rounded-full flex-shrink-0 border-2" style={{ borderColor: role.color || '#99AAB5', backgroundColor: selectedRoleId === role.id && role.color ? role.color : 'transparent' }} />
                  <span className={`text-sm font-medium truncate ${
                    selectedRoleId === role.id ? 'text-text-primary' : 'text-text-secondary'
                  }`}>{role.name}</span>
                  <span className="ml-auto text-[11px] text-text-muted whitespace-nowrap">
                    {role.is_everyone ? 'Default' : `${members.filter((m) => (m.roles || []).some((r) => r.id === role.id)).length} members`}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-text-muted transition-transform ${
                    selectedRoleId === role.id ? 'rotate-90' : ''
                  }`} />
                </button>
              ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Shield className="w-8 h-8 mx-auto text-text-muted mb-2" />
            <p className="text-xs text-text-muted">No roles created yet.</p>
          </div>
        )}

        <div className="px-4 py-2.5 bg-bg-tertiary/30 border-t border-border-color">
          <p className="text-[10px] text-text-muted">
            Roles are assigned to members from the Members tab. The server owner has all permissions by default.
          </p>
        </div>
      </div>

      {/* Role editor — expands below when selected */}
      {selectedRole && (
        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-text-primary flex items-center gap-2">
              <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: editColor || '#99AAB5' }} />
              Editing: {selectedRole.name}
            </h3>
            <div className="flex items-center gap-1.5">
              {canManageRoles && !selectedRole.is_everyone && (
                <button
                  onClick={() => handleDeleteRole(selectedRole.id)}
                  className="p-1.5 rounded-lg hover:bg-error/10 text-text-secondary hover:text-error transition-all"
                  title="Delete role"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setSelectedRoleId(null)}
                className="p-1.5 rounded-lg hover:bg-highlight/50 text-text-secondary hover:text-text-primary transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Name & color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Role Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={selectedRole.is_everyone}
                className="settings-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1.5">Role Color</label>
              {selectedRole.is_everyone ? (
                <div className="h-10 flex items-center px-3 rounded-lg border border-border-color bg-bg-tertiary/40 text-xs text-text-muted">
                  @everyone uses the neutral server color.
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <label
                    className="relative w-10 h-10 rounded-lg border border-border-color bg-bg-primary flex items-center justify-center text-sm font-bold text-text-muted cursor-pointer overflow-hidden"
                    style={{ backgroundColor: editColor || undefined }}
                    title="Pick role color"
                  >
                    {!editColor && '-'}
                  <input
                    type="color"
                      value={editColor || '#99AAB5'}
                    onChange={(e) => setEditColor(e.target.value)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  </label>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={editColor || ''}
                      onChange={(e) => setEditColor(e.target.value.trim() || null)}
                      placeholder="No color"
                      className="settings-input w-full pr-9 font-mono text-xs"
                    />
                    {editColor && (
                      <button
                        type="button"
                        onClick={() => setEditColor(null)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                        title="Clear color"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <label className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-bg-tertiary/50 transition-all ${selectedRole.is_everyone ? 'opacity-60' : 'hover:bg-highlight/30 cursor-pointer'}`}>
            <div>
              <p className="text-xs font-medium text-text-primary">Display separately</p>
              <p className="text-[10px] text-text-muted">Show members with this role in their own member list group.</p>
            </div>
            <div
              onClick={() => !selectedRole.is_everyone && setEditHoist((prev) => !prev)}
              className={`w-9 h-5 rounded-full flex items-center transition-all ${
                editHoist ? 'bg-accent-color justify-end' : 'bg-bg-tertiary border border-border-color justify-start'
              }`}
            >
              <div className={`w-3.5 h-3.5 rounded-full mx-0.5 transition-all ${
                editHoist ? 'bg-black' : 'bg-text-muted'
              }`} />
            </div>
          </label>

          {/* Permissions */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-3">Permissions</label>
            <div className="space-y-4">
              {PERMISSION_GROUPS.map((group) => (
                <section key={group.title} className="space-y-1.5">
                  <h4 className="text-[11px] uppercase tracking-[0.12em] text-text-muted font-bold">{group.title}</h4>
                  {group.items.map((perm) => (
                    <label
                      key={perm.flag}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-bg-tertiary/50 hover:bg-highlight/30 transition-all cursor-pointer"
                    >
                      <div>
                        <p className="text-xs font-medium text-text-primary">{perm.label}</p>
                        <p className="text-[10px] text-text-muted">{perm.desc}</p>
                      </div>
                      <div
                        onClick={() => togglePerm(perm.flag)}
                        className={`w-9 h-5 rounded-full flex items-center transition-all cursor-pointer ${
                          editPerms & perm.flag ? 'bg-accent-color justify-end' : 'bg-bg-tertiary border border-border-color justify-start'
                        }`}
                      >
                        <div className={`w-3.5 h-3.5 rounded-full mx-0.5 transition-all ${
                          editPerms & perm.flag ? 'bg-black' : 'bg-text-muted'
                        }`} />
                      </div>
                    </label>
                  ))}
                </section>
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSaveRole}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesTab;
